'use client';
import type { FC } from 'react';
import { useRef, useState } from 'react';
import { useDevice } from '@/app/hooks';
import { cn } from '@/app/utils';
import { playMediaSafely } from '@/app/utils/client';
import { HOVER_PLAY_DELAY } from '../constants';

interface VideoCardProps {
  creationId: string;
  videoUrl: string;
  posterUrl: string;
  onSelected: (creationId: string) => void;
  onPlayStart?: () => void;
}

export const VideoCard: FC<VideoCardProps> = ({ creationId, videoUrl, posterUrl, onSelected, onPlayStart }) => {
  const { isMobile } = useDevice();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const handleMediaLoaded = () => {
    setIsLoading(false);
  };
  const handleClick = () => {
    onSelected(creationId);
  };
  const handleMouseEnter = () => {
    timerRef.current = setTimeout(() => {
      const video = videoRef.current;
      if (video) {
        playMediaSafely(video);
        onPlayStart?.();
      }
    }, HOVER_PLAY_DELAY);
  };
  const handleMouseLeave = () => {
    const video = videoRef.current;
    if (video) video.pause();
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  return (
    <div onClick={handleClick}>
      {isMobile ? (
        <div className="relative flex aspect-[16/9] w-full justify-center overflow-hidden rounded-br-[20px] rounded-bl-[20px] md:rounded-br-10 md:rounded-bl-10">
          <div
            className="absolute inset-0 bg-[#191D25] bg-center bg-cover blur-md"
            style={{
              backgroundImage: `url(${posterUrl})`,
              transform: 'scale(1.1)',
            }}
          />
          <img src={posterUrl} alt="img" className="relative aspect-[16/9] w-full object-contain" />
        </div>
      ) : (
        <div
          className="relative flex aspect-[16/9] w-full cursor-pointer items-center justify-center overflow-hidden rounded-br-[20px] rounded-bl-[20px] md:rounded-br-10 md:rounded-bl-10"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div
            className="absolute inset-0 bg-[#191D25] bg-center bg-cover blur-md"
            style={{
              backgroundImage: `url(${posterUrl})`,
              transform: 'scale(1.1)',
            }}
          />
          <video
            ref={videoRef}
            preload="none"
            className={cn(
              'relative aspect-[16/9] w-full',
              !isLoading && 'opacity-100',
              'transition-opacity duration-300',
            )}
            muted
            loop
            playsInline
            webkit-playsinline="true"
            x5-playsinline="true"
            src={videoUrl}
            onLoadedData={handleMediaLoaded}
            poster={posterUrl}
          />
        </div>
      )}
    </div>
  );
};
